import { useState, useEffect } from 'react';
import { useAuthStore } from '@/store/authStore';

interface FirestoreDoc {
  id: string;
  path: string;
  data: Record<string, unknown>;
}

export const FirebaseInspector = () => {
  const { user, isAuthenticated } = useAuthStore();
  const [collections] = useState([
    'user_watchlists',
    'user_alerts', 
    'user_preferences',
    'discussions',
    'live_updates',
    'public_data',
  ]);
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [documents, setDocuments] = useState<FirestoreDoc[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<FirestoreDoc | null>(null);
  const [loading, setLoading] = useState(false);
  const [tokenInfo, setTokenInfo] = useState<{
    expiresAt: string | null;
    claims: Record<string, unknown>;
  }>({ expiresAt: null, claims: {} });

  // Get token info
  useEffect(() => {
    const getTokenInfo = async () => {
      try {
        // Try to get Firebase auth instance
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (currentUser) {
          const token = await currentUser.getIdTokenResult();
          setTokenInfo({
            expiresAt: token.expirationTime,
            claims: token.claims,
          });
        }
      } catch {
        // Firebase not initialized or user not logged in
      }
    };
    if (isAuthenticated) {
      getTokenInfo();
    }
  }, [isAuthenticated]);

  const fetchDocuments = async (collectionName: string) => {
    setLoading(true);
    setSelectedCollection(collectionName);
    setSelectedDoc(null);
    
    try {
      const { getFirestore, collection, getDocs, limit, query } = await import('firebase/firestore');
      const db = getFirestore();
      const q = query(collection(db, collectionName), limit(20));
      const snapshot = await getDocs(q);
      
      const docs: FirestoreDoc[] = [];
      snapshot.forEach((doc) => {
        docs.push({
          id: doc.id,
          path: `${collectionName}/${doc.id}`,
          data: doc.data(),
        });
      });
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const rulesCheck = async () => {
    // Simulate rules test
    const tests = [
      { path: '/user_watchlists/{userId}', operation: 'read', result: isAuthenticated },
      { path: '/user_alerts/{alertId}', operation: 'read', result: isAuthenticated },
      { path: '/discussions/{id}', operation: 'read', result: isAuthenticated },
      { path: '/public_data/{doc}', operation: 'read', result: true },
      { path: '/admin_only/{doc}', operation: 'write', result: user?.role === 'admin' },
    ];
    return tests;
  };

  const [rulesResults, setRulesResults] = useState<Array<{path: string; operation: string; result: boolean}>>([]);
  
  useEffect(() => {
    rulesCheck().then(setRulesResults);
  }, [isAuthenticated, user?.role]);

  return (
    <div className="space-y-4">
      {/* Natural Language Summary */}
      <div className="bg-gradient-to-r from-orange-900/50 to-red-900/50 rounded-lg p-3 border border-orange-500/30">
        <p className="text-sm">
          <span className="text-orange-400 font-medium">Firebase Status:</span>{' '}
          <span className="text-gray-200">
            {isAuthenticated ? (
              <>Logged in as <span className="text-green-400">{user?.email}</span></>
            ) : (
              <span className="text-yellow-400">Not authenticated</span>
            )}
            {tokenInfo.expiresAt && (
              <>, token expires {new Date(tokenInfo.expiresAt).toLocaleTimeString()}</>
            )}
          </span>
        </p>
      </div>

      {/* Auth State */}
      <div className="bg-gray-800 rounded-lg p-3">
        <h4 className="text-sm font-medium text-orange-400 mb-2">🔐 Auth State</h4>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="bg-gray-900 rounded p-2">
            <span className="text-gray-500">Status:</span>
            <span className={`ml-2 ${isAuthenticated ? 'text-green-400' : 'text-red-400'}`}>
              {isAuthenticated ? '✅ Authenticated' : '❌ Anonymous'}
            </span>
          </div>
          <div className="bg-gray-900 rounded p-2">
            <span className="text-gray-500">User ID:</span>
            <span className="ml-2 text-gray-300 font-mono text-[10px]">{user?.id || 'N/A'}</span>
          </div>
          <div className="bg-gray-900 rounded p-2">
            <span className="text-gray-500">Email:</span>
            <span className="ml-2 text-gray-300">{user?.email || 'N/A'}</span>
          </div>
          <div className="bg-gray-900 rounded p-2">
            <span className="text-gray-500">Role:</span>
            <span className={`ml-2 ${
              user?.role === 'admin' ? 'text-red-400' :
              user?.role === 'premium_user' ? 'text-purple-400' : 'text-gray-400'
            }`}>
              {user?.role || 'free_user'}
            </span>
          </div>
        </div>
        
        {/* Token Claims */}
        {Object.keys(tokenInfo.claims).length > 0 && (
          <div className="mt-2">
            <span className="text-gray-500 text-xs">Token Claims:</span>
            <pre className="bg-gray-900 rounded p-2 mt-1 text-[10px] overflow-auto max-h-20">
              {JSON.stringify(tokenInfo.claims, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Firestore Browser */}
      <div className="bg-gray-800 rounded-lg p-3">
        <h4 className="text-sm font-medium text-orange-400 mb-2">📚 Firestore Browser</h4>
        
        {/* Collections */}
        <div className="flex flex-wrap gap-1 mb-2">
          {collections.map((col) => (
            <button
              key={col}
              onClick={() => fetchDocuments(col)}
              className={`px-2 py-1 rounded text-xs ${
                selectedCollection === col
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {col}
            </button>
          ))}
        </div>
        
        {/* Documents */}
        {loading ? (
          <div className="text-center py-4 text-gray-500 text-xs">Loading...</div>
        ) : selectedCollection && (
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {documents.length === 0 ? (
              <p className="text-gray-500 text-xs text-center py-2">No documents found</p>
            ) : (
              documents.map((doc) => (
                <div
                  key={doc.id}
                  onClick={() => setSelectedDoc(doc)}
                  className={`p-2 rounded cursor-pointer text-xs ${
                    selectedDoc?.id === doc.id ? 'bg-orange-900/50 border border-orange-500' : 'bg-gray-900 hover:bg-gray-850'
                  }`}
                >
                  <span className="text-orange-400 font-mono">{doc.id}</span>
                </div>
              ))
            )}
          </div>
        )}
        
        {/* Selected Document */}
        {selectedDoc && (
          <div className="mt-2">
            <span className="text-gray-500 text-xs">Document Data:</span>
            <pre className="bg-gray-900 rounded p-2 mt-1 text-[10px] overflow-auto max-h-24">
              {JSON.stringify(selectedDoc.data, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Rules Tester */}
      <div className="bg-gray-800 rounded-lg p-3">
        <h4 className="text-sm font-medium text-orange-400 mb-2">🛡️ Security Rules Check</h4>
        <div className="space-y-1">
          {rulesResults.map((test, i) => (
            <div key={i} className="flex items-center justify-between text-xs bg-gray-900 rounded p-2">
              <span className="text-gray-400">
                <span className="text-purple-400">{test.operation}</span> {test.path}
              </span>
              <span className={test.result ? 'text-green-400' : 'text-red-400'}>
                {test.result ? '✅ Allowed' : '❌ Denied'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

